import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { NavigateFn } from './use-table-url-state'

function parseValue(value: string, key: string): unknown {
  if (key === 'page' || key === 'pageSize') {
    const num = parseInt(value, 10)
    return Number.isNaN(num) ? undefined : num
  }
  if (value.includes(',')) {
    return value.split(',').filter(Boolean)
  }
  return value
}

function searchParamsToRecord(params: URLSearchParams): Record<string, unknown> {
  const record: Record<string, unknown> = {}
  params.forEach((value, key) => {
    const parsed = parseValue(value, key)
    if (parsed !== undefined && parsed !== '') {
      record[key] = parsed
    }
  })
  return record
}

function cleanSearchRecord(record: Record<string, unknown>): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(record)) {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = value
    }
  }
  return cleaned
}

function mergeSearchUpdate(
  current: Record<string, unknown>,
  update:
    | Record<string, unknown>
    | ((
        prev: Record<string, unknown>
      ) => Partial<Record<string, unknown>> | Record<string, unknown>)
): Record<string, unknown> {
  if (typeof update === 'function') {
    const updated = update(current)
    const merged = { ...current, ...updated }
    for (const [key, value] of Object.entries(updated)) {
      if (value === undefined || value === null || value === '') {
        delete merged[key]
      }
    }
    // Updaters spread `prev` then delete keys to clear them; treat omissions as removals.
    for (const key of Object.keys(current)) {
      if (!(key in updated)) {
        delete merged[key]
      }
    }
    return merged
  }
  return cleanSearchRecord({ ...current, ...update })
}

function recordToSearchParams(record: Record<string, unknown>): URLSearchParams {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(record)) {
    if (value === undefined || value === null || value === '') {
      continue
    }
    if (Array.isArray(value)) {
      params.set(key, value.map(String).join(','))
    } else {
      params.set(key, String(value))
    }
  }
  return params
}

export { cleanSearchRecord, mergeSearchUpdate }

/**
 * Returns URL search state and a navigate function compatible with
 * useTableUrlState and other components that expect TanStack Router-style
 * search + navigate.
 */
export function useSearchParamsNavigate(): {
  search: Record<string, unknown>
  navigate: NavigateFn
} {
  const [searchParams, setSearchParams] = useSearchParams()

  const search = useMemo(
    () => searchParamsToRecord(searchParams),
    [searchParams]
  )

  const navigate = useCallback<NavigateFn>(
    (opts) => {
      setSearchParams(
        (currentParams) => {
          const current = searchParamsToRecord(currentParams)
          const merged =
            opts.search === true
              ? {}
              : mergeSearchUpdate(current, opts.search)
          return recordToSearchParams(merged)
        },
        { replace: opts.replace ?? false }
      )
    },
    [setSearchParams]
  )

  return { search, navigate }
}
